import React, { useState, useEffect } from "react";
import { Popover, IconButton, PopoverOrigin } from "@material-ui/core";

import { Icon } from "js/Components/Icon";

import styled, { keyframes } from "styled-components";
import { POPOVER_TASK_CONFIGS } from "./taskConfig";
import QuickStartController from "js/controllers/QuickStartController";
import { PopoverState } from "./TaskRendererFactory";

const ANIMATION_DELAY = 800;
const ANIMATION_DURATION = 600;

const slideInFade = keyframes`
    from {
        opacity: 0;
        transform: translateX(-20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const StyledPopover = styled(Popover)`
    &&& {
        pointer-events: none;
        
        .MuiPaper-root {
            pointer-events: auto;
            animation: ${slideInFade} ${ANIMATION_DURATION}ms ease-out forwards;
        }
    }
`;

const CloseButtonContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1000;
`;

const CloseButton = styled(IconButton)`
    &&& {
        padding: 8px;
    }
`;

const Title = styled.div`
    font-family: "Source Sans Pro";
    font-size: 18px;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: 0.5px;
`;

const Body = styled.div`
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%;
    letter-spacing: -0.32px;
`;

interface ThemePopoverProps {
    open: boolean;
    taskKey?: string;
    popoverState: PopoverState;
    onClose: () => void;
    transformOrigin?: PopoverOrigin;
    onCallback?: () => void;
    saveOnOpen?: boolean;
}

const ContentContainer = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const ThemePopover: React.FC<ThemePopoverProps> = ({ open, taskKey, popoverState, onClose, onCallback, saveOnOpen = true }) => {
    const [isVisible, setIsVisible] = useState(false);
    const { position, transformOrigin } = popoverState;

    // Delay showing the popover by 800ms
    useEffect(() => {
        if (open && position) {
            const timer = setTimeout(() => {
                // We want to update once the popover is visible
                // We need to make sure we're only updating the correct props
                if (taskKey && QuickStartController.taskKeys
                    // We don't want to update the add your first smart slide task
                    .includes(taskKey) &&
                    saveOnOpen) {
                    QuickStartController.updateTask(taskKey, true);
                }
                setIsVisible(true);
            }, ANIMATION_DELAY);
            return () => {
                clearTimeout(timer);
                setIsVisible(false);
            };
        } else {
            setIsVisible(false);
        }
    }, [open, position]);

    if (!open || !position || !isVisible) {
        return null;
    }

    const config = POPOVER_TASK_CONFIGS[taskKey];

    if (!config) {
        return null;
    }

    const { popoverTitle: title, popoverBody: body, img } = config;

    return (
        <StyledPopover
            open={isVisible}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={position}
            transformOrigin={transformOrigin}
            transitionDuration={0}
            PaperProps={{
                style: {
                    width: 280,
                    background: "#FFFFFF",
                    borderRadius: 8,
                    boxShadow: "0 9px 37px 0 rgba(0, 0, 0, 0.20)",
                    border: "1px solid #222",
                    padding: 0,
                    position: "relative",
                    zIndex: 10
                }
            }}
            disableRestoreFocus
            disableEnforceFocus
            disableAutoFocus
            disableBackdropClick
        >
            <CloseButtonContainer>
                <CloseButton
                    onClick={onClose}
                    size="small"
                >
                    <Icon fill white medium>cancel</Icon>
                </CloseButton>
            </CloseButtonContainer>
            <div>
                <div style={{ height: 150 }}><img style={{ width: "100%", height: "100%", objectFit: "contain" }} src={img} /></div>
                <ContentContainer>
                    <Title>
                        {title}
                    </Title>
                    <Body>
                        {body(onCallback)}
                    </Body>
                </ContentContainer>
            </div>
        </StyledPopover>
    );
};

